import { normalizeAvatarName } from "./SpritesMap";
import { storage } from "../firebase/config";

const DEFAULT_AVATAR_IMAGE = require("../assets/avatars/InfintyOrbs/green.png");
const ORB_STORAGE_ROOT = "infinity_orbs";
const ORB_LAYER_ORDER = [
  "backgrounds",
  "shadow",
  "backwear",
  "colors",
  "glass-conditions",
  "expressions",
  "bezel",
  "adornments",
  "aura",
  "headwear",
];
const ORB_LAYER_FOLDERS = new Set(
  ORB_LAYER_ORDER.filter((folder) => folder !== "shadow"),
);
const ORB_FOLDER_ALIASES = {
  adornment: "adornments",
  adornments: "adornments",
  aura: "aura",
  background: "backgrounds",
  backgrounds: "backgrounds",
  backwear: "backwear",
  bezel: "bezel",
  color: "colors",
  colors: "colors",
  expression: "expressions",
  expressions: "expressions",
  "glass condition": "glass-conditions",
  "glass conditions": "glass-conditions",
  "glass-condition": "glass-conditions",
  "glass-conditions": "glass-conditions",
  headwear: "headwear",
  shadow: "shadow",
};
const folderAssetIndexPromiseCache = new Map();

const LAYER_URL_KEYS = [
  "url",
  "uri",
  "src",
  "image",
  "imageUrl",
  "assetUrl",
  "href",
];

const LAYER_NAME_KEYS = [
  "layerName",
  "name",
  "label",
  "category",
  "trait_type",
  "traitType",
  "type",
];

const LAYER_ORDER_KEYS = ["zIndex", "z", "order", "index", "position"];

const KNOWN_LAYER_PATHS = [
  ["layers"],
  ["metadata", "layers"],
  ["metadata", "properties", "layers"],
  ["metadata", "properties", "files"],
  ["properties", "layers"],
  ["properties", "files"],
  ["files"],
];

const pickFirstString = (source, keys = []) => {
  for (const key of keys) {
    const value = source?.[key];
    if (typeof value === "string" && value.trim()) {
      return value.trim();
    }
  }
  return "";
};

const pickFirstNumber = (source, keys = []) => {
  for (const key of keys) {
    const value = Number(source?.[key]);
    if (Number.isFinite(value)) {
      return value;
    }
  }
  return null;
};

const getAtPath = (source, path = []) =>
  path.reduce(
    (value, key) =>
      value && typeof value === "object" ? value[key] : undefined,
    source,
  );

const trimSlashes = (value) => String(value || "").replace(/^\/+|\/+$/g, "");

const removeFileExtension = (value) => String(value || "").replace(/\.[^.]+$/, "");

const normalizeStorageKey = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/['".,!?()[\]{}]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const compactStorageKey = (value) => normalizeStorageKey(value).replace(/-/g, "");

const isBuiltInIoAvatar = (avatar = null) => {
  const normalizedName = normalizeAvatarName(avatar?.name || "");
  const image = String(avatar?.image || "");

  return (
    normalizedName === "io" ||
    normalizedName === "io_default" ||
    normalizedName.startsWith("io_") ||
    normalizedName === "infinty_orb" ||
    normalizedName === "infinity_orb" ||
    normalizedName.startsWith("i_o_") ||
    normalizedName.includes("orb") ||
    image.includes("profile_images%2Fio%20") ||
    image.includes("assets/avatars/InfintyOrbs") ||
    image.includes("InfintyOrbs")
  );
};

const createStorageKeyVariants = (value) => {
  const normalized = normalizeStorageKey(value);
  const compact = compactStorageKey(value);
  return [...new Set([normalized, compact].filter(Boolean))];
};

const normalizeTraitValueLabel = (value) =>
  String(value ?? "")
    .trim()
    .replace(/\s+/g, " ");

const normalizeTraitTypeLabel = (value) =>
  String(value ?? "")
    .trim()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ");

const normalizeAttributeRecord = (attribute) => {
  if (!attribute || typeof attribute !== "object") return null;

  const traitType =
    attribute?.traitType ||
    attribute?.trait_type ||
    attribute?.type ||
    attribute?.name ||
    "";
  const value =
    attribute?.value ??
    attribute?.display_value ??
    attribute?.displayValue ??
    attribute?.name ??
    "";

  if (!traitType && value === "") return null;

  return {
    traitType: normalizeTraitTypeLabel(traitType),
    value: normalizeTraitValueLabel(value),
  };
};

export const normalizeOrbAttributes = (attributes = []) =>
  (Array.isArray(attributes) ? attributes : [])
    .map((attribute) => normalizeAttributeRecord(attribute))
    .filter(Boolean);

const resolveOrbFolderFromTraitType = (traitType) => {
  const key = normalizeStorageKey(normalizeTraitTypeLabel(traitType)).replace(
    /-/g,
    " ",
  );
  const alias = ORB_FOLDER_ALIASES[key];
  if (alias) return alias;

  const folderKey = normalizeStorageKey(traitType);
  return ORB_LAYER_FOLDERS.has(folderKey) ? folderKey : "";
};

const getLayerOrder = (folder) => {
  const index = ORB_LAYER_ORDER.indexOf(folder);
  return index >= 0 ? index : ORB_LAYER_ORDER.length + 1;
};

const buildFolderAssetIndex = async (folder) => {
  try {
    const folderRef = storage.ref(
      `${trimSlashes(ORB_STORAGE_ROOT)}/${trimSlashes(folder)}`,
    );
    const listing = await folderRef.listAll();
    const entries = await Promise.all(
      (listing.items || []).map(async (itemRef) => {
        const name = itemRef?.name || "";
        const baseName = removeFileExtension(name);
        const url = await itemRef.getDownloadURL();
        return {
          name,
          baseName,
          fullPath: itemRef?.fullPath || "",
          url,
        };
      }),
    );

    const index = new Map();
    entries.forEach((entry) => {
      createStorageKeyVariants(entry.baseName).forEach((key) => {
        if (!index.has(key)) {
          index.set(key, entry);
        }
      });
    });

    return index;
  } catch (error) {
    console.warn(`Failed to index orb trait folder "${folder}":`, error);
    return new Map();
  }
};

const getFolderAssetIndex = async (folder) => {
  const normalizedFolder = trimSlashes(folder);
  if (!normalizedFolder) return new Map();

  if (!folderAssetIndexPromiseCache.has(normalizedFolder)) {
    folderAssetIndexPromiseCache.set(
      normalizedFolder,
      buildFolderAssetIndex(normalizedFolder),
    );
  }

  return folderAssetIndexPromiseCache.get(normalizedFolder);
};

const findFolderAssetMatch = async (folder, traitValue) => {
  const index = await getFolderAssetIndex(folder);
  const keys = createStorageKeyVariants(traitValue);
  for (const key of keys) {
    if (index.has(key)) {
      return index.get(key);
    }
  }
  return null;
};

const isLikelyLayerLike = (item) => {
  if (!item || typeof item !== "object" || Array.isArray(item)) return false;
  return !!pickFirstString(item, LAYER_URL_KEYS);
};

const normalizeLayer = (layer, index = 0) => {
  if (!isLikelyLayerLike(layer)) return null;

  const url = pickFirstString(layer, LAYER_URL_KEYS);
  if (!url) return null;

  const name = pickFirstString(layer, LAYER_NAME_KEYS) || `Layer ${index + 1}`;
  const category =
    layer?.category ||
    layer?.group ||
    layer?.trait_type ||
    layer?.traitType ||
    layer?.type ||
    "";
  const order = pickFirstNumber(layer, LAYER_ORDER_KEYS);

  return {
    id: String(layer?.id || layer?.layerId || `${name}-${index}`),
    name,
    category: String(category || ""),
    url,
    order: order ?? index,
    raw: layer,
  };
};

export const isBackgroundLayer = (layer) => {
  const joined = `${layer?.name || ""} ${layer?.category || ""}`.toLowerCase();
  return joined.includes("background");
};

export const normalizeOrbLayers = (layers = []) =>
  (Array.isArray(layers) ? layers : [])
    .map((layer, index) => normalizeLayer(layer, index))
    .filter(Boolean)
    .filter((layer) => !isBackgroundLayer(layer))
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

const scoreLayerCandidate = (path = "", layers = []) => {
  const lowerPath = path.toLowerCase();
  let score = layers.length;
  if (lowerPath.includes("layer")) score += 50;
  if (lowerPath.includes("metadata")) score += 10;
  if (lowerPath.includes("properties")) score += 5;
  if (lowerPath.includes("file")) score -= 2;
  return score;
};

const collectLayerCandidates = (source, path = "root", depth = 0, seen = new Set()) => {
  if (!source || typeof source !== "object" || depth > 6 || seen.has(source)) {
    return [];
  }

  seen.add(source);

  if (Array.isArray(source)) {
    const layers = normalizeOrbLayers(source);
    const nested = source.flatMap((item, index) =>
      collectLayerCandidates(item, `${path}[${index}]`, depth + 1, seen),
    );
    return layers.length ? [{ path, layers }, ...nested] : nested;
  }

  return Object.entries(source).flatMap(([key, value]) =>
    collectLayerCandidates(value, `${path}.${key}`, depth + 1, seen),
  );
};

export const extractOrbLayersFromAsset = (asset) => {
  const explicitCandidates = KNOWN_LAYER_PATHS.map((path) => {
    const layers = normalizeOrbLayers(getAtPath(asset, path));
    return layers.length ? { path: path.join("."), layers } : null;
  }).filter(Boolean);

  const recursiveCandidates = collectLayerCandidates(asset);
  const allCandidates = [...explicitCandidates, ...recursiveCandidates];

  if (!allCandidates.length) return [];

  return [...allCandidates]
    .sort(
      (a, b) =>
        scoreLayerCandidate(b.path, b.layers) -
        scoreLayerCandidate(a.path, a.layers),
    )[0]
    ?.layers || [];
};

export const buildOrbLayersFromAttributes = async (attributes = []) => {
  const normalizedAttributes = normalizeOrbAttributes(attributes);
  if (!normalizedAttributes.length) return [];

  const resolvedLayers = await Promise.all(
    normalizedAttributes.map(async (attribute, index) => {
      const folder = resolveOrbFolderFromTraitType(attribute.traitType);
      if (!folder || folder === "shadow" || folder === "backgrounds") {
        return null;
      }

      const asset = await findFolderAssetMatch(folder, attribute.value);
      if (!asset?.url) {
        console.warn(
          `Missing Infinity Orb layer asset for ${attribute.traitType}: ${attribute.value}`,
        );
        return null;
      }

      return {
        id: `${folder}-${normalizeStorageKey(attribute.value) || index}`,
        name: attribute.value,
        category: folder,
        value: attribute.value,
        url: asset.url,
        order: getLayerOrder(folder),
        storagePath: asset.fullPath,
        raw: attribute,
      };
    }),
  );

  return normalizeOrbLayers(resolvedLayers.filter(Boolean));
};

const METADATA_URL_KEYS = [
  "tokenUri",
  "tokenURI",
  "metadataUrl",
  "metadataURI",
  "metadataUri",
  "uri",
];

export const extractMetadataUrlFromAsset = (asset) => {
  const direct = pickFirstString(asset, METADATA_URL_KEYS);
  if (direct) return direct;

  const nestedCandidates = [
    asset?.metadata,
    asset?.token,
    asset?.tokenMetadata,
    asset?.metadataProperties,
  ];

  for (const candidate of nestedCandidates) {
    const url = pickFirstString(candidate, METADATA_URL_KEYS);
    if (url) return url;
  }

  return "";
};

export const fetchOrbMetadataJson = async (asset) => {
  const metadataUrl = extractMetadataUrlFromAsset(asset);
  if (!metadataUrl) return null;

  try {
    const res = await fetch(metadataUrl);
    if (!res.ok) return null;
    const contentType = res.headers.get("content-type") || "";
    if (!contentType.toLowerCase().includes("json")) return null;
    return await res.json();
  } catch (error) {
    console.warn("Failed to load orb metadata JSON:", metadataUrl, error);
    return null;
  }
};

const coerceAvatarSource = (source) => {
  if (!source || typeof source !== "object") return {};

  if (source?.orbAvatar || source?.avatar) {
    return source;
  }

  if (source?.tokenId || source?.layers || source?.composedImage) {
    return { orbAvatar: source };
  }

  if (source?.name || source?.image || source?.ws) {
    return { avatar: source };
  }

  return {};
};

export const resolveAvatarData = (source) => {
  const value = coerceAvatarSource(source);
  const orbAvatar = value?.orbAvatar || null;
  const avatar = value?.avatar || null;
  const hasBuiltInIoAvatar = isBuiltInIoAvatar(avatar);
  const hasOrbAvatar =
    !!orbAvatar &&
    !!(
      orbAvatar?.tokenId ||
      orbAvatar?.layers?.length ||
      orbAvatar?.composedImage ||
      orbAvatar?.composedImageUrl ||
      orbAvatar?.imageUrl
    );

  if (hasOrbAvatar || hasBuiltInIoAvatar) {
    const image =
      orbAvatar?.composedImage ||
      orbAvatar?.composedImageUrl ||
      orbAvatar?.imageUrl ||
      orbAvatar?.image ||
      avatar?.image ||
      DEFAULT_AVATAR_IMAGE;

    return {
      isOrbAvatar: true,
      usesStaticImageOverlay: hasOrbAvatar,
      name: orbAvatar?.name || avatar?.name || "Infinity Orb",
      image,
      ws: avatar?.ws || "",
      layers: normalizeOrbLayers(orbAvatar?.layers || []),
      normalizedName: normalizeAvatarName(
        orbAvatar?.name || avatar?.name || "orb_avatar"
      ),
      avatar,
      orbAvatar,
    };
  }

  return {
    isOrbAvatar: false,
    usesStaticImageOverlay: false,
    name: avatar?.name || "Infinity Orb",
    image: avatar?.image || DEFAULT_AVATAR_IMAGE,
    ws: avatar?.ws || "",
    layers: [],
    normalizedName: normalizeAvatarName(avatar?.name || "io_default"),
    avatar,
    orbAvatar: null,
  };
};

export const getAvatarImage = (source) => resolveAvatarData(source).image;

const loadLayerImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = "anonymous";
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error(`Failed to load layer image: ${url}`));
    image.src = url;
  });

export const composeOrbAvatarFromLayers = async (
  layers = [],
  { fallbackImageUrl = "", preferredSize = 0 } = {},
) => {
  if (typeof document === "undefined") {
    return fallbackImageUrl || "";
  }

  const normalizedLayers = normalizeOrbLayers(layers);
  if (!normalizedLayers.length) {
    return fallbackImageUrl || "";
  }

  try {
    const loadedLayers = await Promise.all(
      normalizedLayers.map(async (layer) => ({
        layer,
        image: await loadLayerImage(layer.url),
      })),
    );

    const width =
      preferredSize ||
      Math.max(...loadedLayers.map(({ image }) => image.naturalWidth || 0), 256);
    const height =
      preferredSize ||
      Math.max(
        ...loadedLayers.map(({ image }) => image.naturalHeight || 0),
        256,
      );

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;

    const context = canvas.getContext("2d");
    if (!context) return fallbackImageUrl || "";

    loadedLayers.forEach(({ image }) => {
      context.drawImage(image, 0, 0, width, height);
    });

    return canvas.toDataURL("image/png");
  } catch (error) {
    console.warn("Failed to compose orb avatar from layers:", error);
    return fallbackImageUrl || "";
  }
};

export const createOrbTextureKey = (orbAvatar) => {
  const base = orbAvatar?.tokenId || orbAvatar?.name || "selected-orb";
  return `orb-avatar-${String(base).replace(/[^a-z0-9_-]/gi, "_").toLowerCase()}`;
};

export const describeOrbSelection = (orb) =>
  orb?.name || (orb?.tokenId ? `Infinity Orb #${orb.tokenId}` : "Infinity Orb");

export const getOrbLayerSourceMeta = (orb = {}) => {
  const source = String(orb?.layerSource || "").toLowerCase();

  if (source === "storage") {
    return {
      source,
      label: "Available",
      detail: `${orb?.layers?.length || 0} mapped layers ready`,
      isAvailable: true,
    };
  }

  if (source === "metadata") {
    return {
      source,
      label: "Available",
      detail: `${orb?.layers?.length || 0} metadata layers ready`,
      isAvailable: true,
    };
  }

  return {
    source: source || "attributes",
    label: "Not Available",
    detail: "Avatar layers not available yet",
    isAvailable: false,
  };
};

export const normalizeOrbRecord = (record = {}) => {
  const layers = normalizeOrbLayers(record?.layers || []);
  return {
    tokenId: record?.tokenId ? String(record.tokenId) : "",
    name: describeOrbSelection(record),
    imageUrl: record?.imageUrl || "",
    layers,
    layerSource: record?.layerSource || "",
    composedImage: record?.composedImage || record?.composedImageUrl || "",
    verifiedAt: record?.verifiedAt || null,
  };
};
